import { createSelector } from '@reduxjs/toolkit';
import { snakeToCamelCase } from 'services/helpers';
import { doFilterSetsHaveError } from 'ts-filters/utils';
import UUID from 'utility/UUID';

export const getIsContactsFetching = ({ contactPage }) =>
  contactPage.isFetchingContacts;
export const getNeedToLoadCountsSelector = ({ contactPage }) =>
  contactPage.needToLoadCounts;
export const getIsContactsFiltering = ({ contactPage }) =>
  contactPage.isFiltering;
export const getIsLoadingView = ({ contactPage }) => contactPage.isLoadingView;
export const getContactsPageReady = ({ contactPage }) => contactPage.pageReady;
export const getFilterGroupReason = ({ contactPage }) =>
  contactPage.filterGroupReason;

export const getIsInitialLoading = (state) => {
  return state.contactPage.fetchedCriteria === undefined;
};

export const getHasFilterError = (state) => {
  const filters = state.contactPage.filters.config;
  const errors = state.contactPage.filters.errors;

  if (!filters || filters.query) {
    return false;
  }

  const errorsWhenLoadingSteps = doFilterSetsHaveError(filters);
  const errorsWhenPerformingSearch = Boolean(
    errors?.parsedErrors.some(Boolean)
  );

  return errorsWhenLoadingSteps || errorsWhenPerformingSearch;
};

const selectDefaultFields = (state) => {
  return state.contactPage.allFields.filter((field) => {
    return field.isDefault;
  });
};

const selectFieldIdsForMobile = (state) => {
  return state.contactPage.allFields.reduce((acc, field) => {
    if (
      ['email', 'first_name', 'last_name', 'mobile_phone'].includes(field.name)
    ) {
      acc.push(field.id);
    }
    return acc;
  }, []);
};

export const selectColumnFieldIds = createSelector(
  selectFieldIdsForMobile,
  selectDefaultFields,
  (state) => state.contactPage.pageConfig.columnOrder,
  (state) => state.contactPage.clientObject,
  (mobileIds, fields, columnOrder = [], model) => {
    const defaultFieldIds = fields.reduce((acc, field) => {
      acc[snakeToCamelCase(field.name)] = field.id;
      return acc;
    }, {});

    if (columnOrder.length === 0) {
      let columns = [
        'firstName',
        'lastName',
        'email',
        'mobilePhone',
        'titles',
        'tags',
        'created',
        'updated',
      ];

      if (model.meta?.defaultColumns?.length) {
        columns = model.meta.defaultColumns.map((column) => {
          return column.id;
        });
      }

      // there may not be view permissions for a default field
      const ids = columns.reduce((acc, name) => {
        if (name === 'fullName') {
          if (defaultFieldIds.firstName) {
            acc.push(defaultFieldIds.firstName);
          }
          if (defaultFieldIds.lastName) {
            acc.push(defaultFieldIds.lastName);
          }
        } else if (defaultFieldIds[name]) {
          acc.push(defaultFieldIds[name]);
        } else if (UUID.validate(name)) {
          acc.push(name);
        }
        return acc;
      }, []);

      return ids;
    }

    // there may not be view permissions for a default field
    const ids = columnOrder.reduce((acc, idOrName) => {
      if (idOrName === 'fullName') {
        acc.add(defaultFieldIds.firstName, defaultFieldIds.lastName);
      } else if (defaultFieldIds[idOrName]) {
        acc.add(defaultFieldIds[idOrName]);
      } else if (UUID.validate(idOrName)) {
        acc.add(idOrName);
      }

      return acc;
    }, new Set(mobileIds));

    return [...ids];
  }
);

export const selectIsFilterFromUrl = (state) => {
  const pageConfig = state.contactPage.pageConfig;

  return (
    Boolean(pageConfig.queryFilter) &&
    Object.keys(pageConfig.queryFilter).length > 0
  );
};
